import React, { useState, useEffect, useCallback } from 'react';
import { Gate } from '../AccessStudio';

// Manage daycare classes (name + teacher + active) and assign children to a
// class. Independent classes — a class may mix packages. Teacher is a system
// user (they fill only their own class). See DAYCARE_DIARY_DESIGN.md.

const DaycareClassManagerModal = ({ isOpen, onClose, onSuccess, apiCall, showError, showSuccess }) => {
  const [classes, setClasses] = useState([]);
  const [staff, setStaff] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [newTeacher, setNewTeacher] = useState('');
  const [assignClassId, setAssignClassId] = useState('');
  const [picked, setPicked] = useState([]);   // childIds to move

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cls, stf, kids] = await Promise.all([
        apiCall('/daycare/classes'),
        apiCall('/daycare/classes/staff'),
        apiCall('/daycare/classes/children-index'),
      ]);
      setClasses(cls || []);
      setStaff(stf || []);
      setChildren(kids || []);
    } catch (e) {
      showError('Error loading classes: ' + e.message);
    } finally { setLoading(false); }
  }, [apiCall, showError]);

  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  const createClass = async () => {
    if (!newName.trim()) return;
    try {
      await apiCall('/daycare/classes', { method: 'POST', body: JSON.stringify({ className: newName, teacherUserId: newTeacher || null }) });
      setNewName(''); setNewTeacher('');
      showSuccess('Class created');
      await load(); onSuccess && onSuccess();
    } catch (e) { showError('Error creating class: ' + e.message); }
  };

  const saveClass = async (c) => {
    try {
      await apiCall(`/daycare/classes/${c.ClassID}`, {
        method: 'PUT',
        body: JSON.stringify({ className: c.ClassName, teacherUserId: c.TeacherUserID || null, isActive: c.IsActive }),
      });
      showSuccess('Class updated');
      await load(); onSuccess && onSuccess();
    } catch (e) { showError('Error updating class: ' + e.message); }
  };

  const editField = (id, field, value) =>
    setClasses((arr) => arr.map((c) => c.ClassID === id ? { ...c, [field]: value } : c));

  const assign = async () => {
    if (!assignClassId || picked.length === 0) return;
    try {
      await apiCall(`/daycare/classes/${assignClassId}/children`, { method: 'PUT', body: JSON.stringify({ childIds: picked }) });
      showSuccess(`Assigned ${picked.length} child(ren)`);
      setPicked([]);
      await load(); onSuccess && onSuccess();
    } catch (e) { showError('Error assigning children: ' + e.message); }
  };

  const classNameById = (id) => (classes.find((c) => c.ClassID === id) || {}).ClassName || '—';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-r from-slate-600 to-slate-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Daycare Classes</h2>
          <button onClick={onClose} className="text-white hover:text-gray-200 text-2xl leading-none">&times;</button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-gray-500">Loading…</div>
        ) : (
          <div className="p-6 overflow-y-auto space-y-6">
            {/* Classes */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Classes</h3>
              <div className="space-y-2">
                {classes.map((c) => (
                  <div key={c.ClassID} className="flex items-center gap-2 flex-wrap">
                    <input value={c.ClassName} onChange={(e) => editField(c.ClassID, 'ClassName', e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-sm w-40" />
                    <select value={c.TeacherUserID || ''} onChange={(e) => editField(c.ClassID, 'TeacherUserID', e.target.value ? Number(e.target.value) : null)}
                      className="border border-gray-300 rounded px-2 py-1 text-sm">
                      <option value="">— teacher —</option>
                      {staff.map((s) => <option key={s.UserID} value={s.UserID}>{s.FullName || s.Username}</option>)}
                    </select>
                    <label className="text-sm text-gray-600 flex items-center gap-1">
                      <input type="checkbox" checked={!!c.IsActive} onChange={(e) => editField(c.ClassID, 'IsActive', e.target.checked)} /> Active
                    </label>
                    <span className="text-xs text-gray-400">{c.ChildCount} kids</span>
                    <Gate permission="daycare.classes.edit" label="Save class">
                      <button onClick={() => saveClass(c)} className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700">Save</button>
                    </Gate>
                  </div>
                ))}
              </div>
              {/* Add new */}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New class name"
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-40" />
                <select value={newTeacher} onChange={(e) => setNewTeacher(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm">
                  <option value="">— teacher —</option>
                  {staff.map((s) => <option key={s.UserID} value={s.UserID}>{s.FullName || s.Username}</option>)}
                </select>
                <Gate permission="daycare.classes.create" label="Add class">
                  <button onClick={createClass} className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700">+ Add class</button>
                </Gate>
              </div>
            </div>

            {/* Assign children */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-800 mb-2">Assign children</h3>
              <div className="flex items-center gap-2 mb-2">
                <select value={assignClassId} onChange={(e) => setAssignClassId(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm">
                  <option value="">Move selected to…</option>
                  {classes.map((c) => <option key={c.ClassID} value={c.ClassID}>{c.ClassName}</option>)}
                </select>
                <Gate permission="daycare.classes.edit" label="Assign children">
                  <button onClick={assign} disabled={!assignClassId || picked.length === 0}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:bg-gray-300">
                    Assign ({picked.length})
                  </button>
                </Gate>
              </div>
              <div className="max-h-52 overflow-y-auto border rounded divide-y">
                {children.map((k) => (
                  <label key={k.ChildID} className="flex items-center gap-2 text-sm px-3 py-1.5">
                    <input type="checkbox" checked={picked.includes(k.ChildID)}
                      onChange={(e) => setPicked((p) => e.target.checked ? [...p, k.ChildID] : p.filter((x) => x !== k.ChildID))} />
                    <span className="flex-1">{k.LastName} {k.FirstName}</span>
                    <span className="text-xs text-gray-400">{k.ClassID ? classNameById(k.ClassID) : 'unassigned'}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end px-6 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50">Close</button>
        </div>
      </div>
    </div>
  );
};

export default DaycareClassManagerModal;
